import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Printer } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

type Props = {
  userName: string;    // 表示する利用者名
  loginId: string;     // 表示するログインID
  newPassword: string; // 生成された仮パスワード
  onClose: () => void; // 閉じるボタンクリック時のハンドラー
};

/**
 * パスワード確認・印刷用モーダル
 * 印刷時に他のDOM要素が干渉しないよう React Portal を使用
 */
export const PasswordConfirmationModal = ({ userName, loginId, newPassword, onClose }: Props) => {
  
  /** * 実行環境のベースURL（オリジン）を取得してログインパスを付与
   * window.location.origin を使用することで、開発環境なら localhost、
   * 本番環境ならそのドメイン名を動的に取得します。
   */
  //const loginUrl = `${window.location.origin}/login`;
  const loginUrl = "https://e-bid.chaichiba.co.jp"; // 動的取得ではなく固定値に変更

  useEffect(() => {
    // モーダル表示中の背面スクロールを禁止（スクロール連動防止）
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  /** ブラウザの印刷ダイアログを呼び出す */
  const handlePrint = () => {
    window.print();
  };

  return createPortal(
    /* 外側コンテナ：画面全体を覆うオーバーレイ */
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 print:absolute print:inset-0 print:bg-white print:p-0 print:m-0 print:flex print:justify-center print:items-start">
      
      {/* モーダル本体
        - max-h-full: 画面の高さを超えないように制限（iPhone SE等の小画面対策）
        - overflow-y-auto: 内容が画面高さを超えた場合に内部スクロールを可能にする
        - my-auto: 内容が短いときは中央に、長いときはスクロールの起点を上端にする
      */}
      <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl overflow-y-auto max-h-full print:overflow-visible print:shadow-none print:w-[80%] print:mt-20 print:border-none my-auto">
        
        {/* ヘッダー：sticky を指定してスクロール時も「閉じる」ボタンを上部に固定 */}
        <div className="flex justify-between items-center px-6 py-4 bg-slate-50 border-b border-slate-200 sticky top-0 z-10 print:hidden">
          <h3 className="font-bold text-slate-700">パスワード発行確認</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* 印刷用タイトル：印刷時にのみ最上部に表示 */}
        <div className="hidden print:block text-center pt-0 pb-4 border-b-2 border-slate-800 mb-8">
          <h2 className="text-2xl font-bold text-slate-900">
            <p>静岡茶市場電子入札システム</p>
            <p>ログイン情報通知書</p>
            </h2>
        </div>

        {/* コンテンツエリア */}
        <div className="p-8 space-y-6 print:p-0">
          {/* 利用者情報エリア */}
          <div className="space-y-2 pb-2 px-4">
          <div className="flex font-bold text-slate-800 items-baseline">
            <span className="text-s shrink-0">氏名：</span>
            <span className="text-xl break-all">{userName}</span>
          </div>
        </div>

          {/* ID・パスワード表示エリア（目立たせるために背景色を適用） */}
          <div className="bg-yellow-50 border-2 border-dashed border-yellow-200 p-6 rounded-lg font-bold print:bg-white print:border-slate-300">
            <div className="flex space-y-2 pb-3 items-baseline">
              <span className="text-s text-yellow-700 w-25 print:text-slate-600 shrink-0">ログインID ：</span>
              <span className="text-4xl font-mono text-slate-900 break-all">{loginId}</span>
            </div>

            <div className="flex space-y-2 items-baseline">
              <span className="text-s text-yellow-700 w-25 print:text-slate-600 shrink-0">仮パスワード：</span>
              <span className="text-4xl font-mono text-slate-900 break-all">{newPassword}</span>
            </div>
          </div>

          {/* 注意事項：警告のため赤色で表示 */}
          <p className="text-s text-red-500 leading-relaxed text-center print:hidden">
            ※この画面を閉じるとパスワードは再表示できません。<br />
          </p>

          {/* URLとQR */}
          <div className="hidden print:block space-y-2 pb-4 text-center">
              <span className="text-s shrink-0">ログインURL：</span>
              <span className="text-xl break-all font-bold">{loginUrl}</span>
            {/* QRコード生成部分 */}
            <div className="flex justify-center p-5 bg-white border border-slate-200 rounded-lg mt-6 print:border-none print:p-0">
              <QRCodeSVG 
                value={loginUrl} 
                size={100} 
                level="M" 
              />
            </div>
            {/* 手順説明 */}
            <div className="mt-8 text-base text-slate-600 leading-relaxed text-left px-4">
              <p className="font-bold border-b border-slate-200 mb-2 pb-1">【ログイン手順】</p>
              <p>１．上記URLもしくはQRコードから、入札システムにアクセスしてください。</p>
              <p>２．本通知書に記載された「ログインID」と「仮パスワード」を入力し、</p>
              <p>　　ログインしてください。</p>
              <p>３．初回ログイン時はパスワード変更が必須です。</p>
              <p>　　以下の条件を満たす新しいパスワードを設定してください。</p>
              
              {/* 手順説明：パスワード設定条件の部分 */}
              <div className="bg-slate-50 p-3 my-2 rounded border border-slate-200">
                <p className="font-bold">＜パスワード設定条件＞</p>
                <div className="mt-2 space-y-2">
                  {/* 1行目 */}
                  <div className="flex">
                    <span className="shrink-0 mr-2">・</span>
                    <span>文字数：10文字以上、128文字以内</span>
                  </div>
                  {/* 2行目 */}
                  <div className="flex">
                    <span className="shrink-0 mr-2">・</span>
                    <div className="flex flex-wrap">
                      <span className="shrink-0">文字種：</span>
                      <div className="flex-1">
                        半角のアルファベット大文字、小文字、数字、記号の内から
                        <br />
                        <span className="font-bold text-red-600 underline">3種類以上</span>
                        を使用
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="mt-4 ">※変更後のパスワードはご自身で厳重に管理してください。</p>
              <p>※パスワードを紛失した場合は、静岡茶市場までお問い合わせください。</p>
            </div>
          </div>

          {/* アクションボタン（印刷） */}
          <div className="flex justify-end pt-4 print:hidden">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg font-bold shadow-lg transition-all"
            >
              <Printer size={18} />
              印刷
            </button>
          </div>
        </div>
      </div>

      {/* 印刷用グローバルスタイル */}
      <style>{`
        @media print {
          /* 印刷時にPortalコンテナ以外の全ての背景要素を非表示にする */
          body > *:not(.fixed) {
            display: none !important;
          }

          /* 印刷用紙の中央に配置するための設定 */
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
            display: flex !important;
            justify-content: center !important;
          }

          /* ブラウザ標準のヘッダー・フッター（日付やURL）を消す設定 */
          @page {
            margin: 0;
            size: portrait;
          }
        }
      `}</style>
    </div>,
    document.body
  );
};